import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { CurrencySelector } from '@/components/ui/currency-selector'
import { useToast } from '@/hooks/use-toast'
import { useCreateAccount, useUsers, useAccounts } from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { DuplicateWarning } from '@/components/DuplicateWarning'
import { AccountResearchPanel } from '@/components/AccountResearchPanel'
import { useUser } from '@/contexts/UserContext'
import { ownerPickerOptions } from '@/lib/salesReps'
import type { Account } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateAccountModalProps {
  open: boolean
  onCreated?: (account: Account) => void
  onOpenChange: (open: boolean) => void
}

export function CreateAccountModal({ open, onOpenChange, onCreated }: CreateAccountModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateAccount()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })

  const accountTypes = usePicklistOptions('account', 'type')
  const accountStatuses = usePicklistOptions('account', 'status')
  const industries = usePicklistOptions('account', 'industry')
  const digitalPolicies = usePicklistOptions('account', 'digital_policy')
  const digitalAccessibilityProficiencies = usePicklistOptions(
    'account',
    'digital_accessibility_proficiency'
  )
  const digitalAccessibilityVendors = usePicklistOptions('account', 'digital_accessibility_vendor')

  const [formData, setFormData] = useState<Partial<Account>>({
    name: '',
    owner_id: currentUser?.id || '',
    status: 'Prospect',
    type: '',
    currency_iso_code: 'USD',
    annual_revenue: undefined,
    phone: '',
    website: '',
    industry: '',
    parent_account_id: '',
    // Billing Address
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
    // Shipping Address
    shipping_street: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: '',
    // Additional Information
    number_of_locations__c: undefined,
    digital_policy__c: '',
    campaign__c: '',
    type_of_shop__c: '',
    digital_accessibility_proficiency__c: '',
    current_digital_accessibility_vendor__c: '',
    house_account__c: false,
    // Description
    description: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const users = usersData?.data || []
  const accounts = accountsData?.data || []

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Account name is required'
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (formData.phone.trim().length > 40) {
      // Matches core_crm.accounts.phone varchar(40) and the backend validator.
      newErrors.phone = 'Phone number must be 40 characters or less'
    }

    if (!formData.billing_street?.trim()) {
      newErrors.billing_street = 'Street address is required'
    }
    if (!formData.billing_city?.trim()) {
      newErrors.billing_city = 'City is required'
    }
    if (!formData.billing_state?.trim()) {
      newErrors.billing_state = 'State/Province is required'
    }
    if (!formData.billing_postal_code?.trim()) {
      newErrors.billing_postal_code = 'Postal code is required'
    }
    if (!formData.billing_country?.trim()) {
      newErrors.billing_country = 'Country is required'
    }

    if (formData.website && formData.website.trim().length > 0) {
      const urlRegex = /^https?:\/\/.+/
      if (!urlRegex.test(formData.website)) {
        newErrors.website = 'Website must be a valid URL starting with http:// or https://'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await createMutation.mutateAsync(formData)

      toast({
        title: 'Success',
        description: 'Account created successfully.',
      })

      if (onCreated && result?.id) {
        onCreated(result as Account)
      }

      // Reset form and close modal
      setFormData({
        name: '',
        owner_id: currentUser?.id || '',
        status: 'Prospect',
        type: '',
        currency_iso_code: 'USD',
        annual_revenue: undefined,
        phone: '',
        website: '',
        industry: '',
        parent_account_id: '',
        billing_street: '',
        billing_city: '',
        billing_state: '',
        billing_postal_code: '',
        billing_country: '',
        shipping_street: '',
        shipping_city: '',
        shipping_state: '',
        shipping_postal_code: '',
        shipping_country: '',
        number_of_locations__c: undefined,
        digital_policy__c: '',
        campaign__c: '',
        type_of_shop__c: '',
        digital_accessibility_proficiency__c: '',
        current_digital_accessibility_vendor__c: '',
        house_account__c: false,
        description: '',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create account')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const selectClassName =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-account-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Account</DialogTitle>
          <DialogDescription id="create-account-description">
            Fill in the details to create a new account. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Website research - proposes values, applies none without a click */}
          <AccountResearchPanel
            onApply={(values) => {
              setFormData((prev) => ({ ...prev, ...values }))
              // Clear stale validation errors on the fields we just filled.
              setErrors((prev) => {
                const next = { ...prev }
                for (const key of Object.keys(values)) delete next[key]
                return next
              })
            }}
          />

          {/* About Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              About
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Account Name */}
              <div>
                <Label htmlFor="name">Account Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter account name"
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Account Owner */}
              <div>
                <Label htmlFor="owner_id">Account Owner</Label>
                <select
                  id="owner_id"
                  value={formData.owner_id}
                  onChange={(e) => handleChange('owner_id', e.target.value)}
                  className={selectClassName}
                  aria-label="Account owner"
                >
                  <option value="">Select owner...</option>
                  {ownerPickerOptions(users, formData.owner_id).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className={selectClassName}
                  aria-label="Account status"
                >
                  {accountStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className={selectClassName}
                  aria-label="Account type"
                >
                  <option value="">Select type...</option>
                  {accountTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency */}
              <div>
                <Label htmlFor="currency_iso_code">Currency</Label>
                <CurrencySelector
                  id="currency_iso_code"
                  hideOther
                  value={formData.currency_iso_code}
                  onValueChange={(v) => handleChange('currency_iso_code', v)}
                  className="mt-1"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  aria-required="true"
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? 'phone-error' : undefined}
                />
                {errors.phone && (
                  <p id="phone-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Annual Revenue */}
              <div>
                <Label htmlFor="annual_revenue">Annual Revenue</Label>
                <Input
                  id="annual_revenue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.annual_revenue ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'annual_revenue',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  placeholder="0.00"
                  aria-label="Annual revenue"
                />
              </div>

              {/* Industry */}
              <div>
                <Label htmlFor="industry">Industry</Label>
                <select
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  className={selectClassName}
                  aria-label="Industry"
                >
                  <option value="">Select industry...</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>

              {/* Website */}
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://example.com"
                  aria-invalid={!!errors.website}
                  aria-describedby={errors.website ? 'website-error' : undefined}
                />
                {errors.website && (
                  <p id="website-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.website}
                  </p>
                )}
              </div>

              {/* Parent Account */}
              <div>
                <Label htmlFor="parent_account">Parent Account</Label>
                <RecordCombobox
                  id="parent_account"
                  records={[...accounts].sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                  value={formData.parent_account_id || ''}
                  onValueChange={(v) => handleChange('parent_account_id', v)}
                  displayField="name"
                  placeholder="Select parent account..."
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No accounts found."
                  aria-label="Parent account"
                  totalCount={accountsData?.total}
                />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Address
            </h3>

            <div className="grid grid-cols-2 gap-6">
              {/* Billing Address */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Billing Address</h4>

                <AddressAutocomplete
                  id="billing_address"
                  label="Billing Street *"
                  value={formData.billing_street || ''}
                  onAddressSelect={(address: AddressDetails) => {
                    setFormData((prev) => ({
                      ...prev,
                      billing_street: address.street,
                      billing_city: address.city,
                      billing_state: address.state,
                      billing_postal_code: address.postal_code,
                      billing_country: address.country,
                    }))
                  }}
                  onStreetChange={(street) => handleChange('billing_street', street)}
                  placeholder="Start typing address..."
                />
                {errors.billing_street && (
                  <p
                    id="billing-street-error"
                    className="text-sm text-destructive mt-1"
                    role="alert"
                  >
                    {errors.billing_street}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="billing_city">Billing City *</Label>
                    <Input
                      id="billing_city"
                      value={formData.billing_city}
                      onChange={(e) => handleChange('billing_city', e.target.value)}
                      placeholder="City"
                      aria-required="true"
                      aria-invalid={!!errors.billing_city}
                      aria-describedby={errors.billing_city ? 'billing-city-error' : undefined}
                    />
                    {errors.billing_city && (
                      <p
                        id="billing-city-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.billing_city}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="billing_state">Billing State/Province *</Label>
                    <Input
                      id="billing_state"
                      value={formData.billing_state}
                      onChange={(e) => handleChange('billing_state', e.target.value)}
                      placeholder="State"
                      aria-required="true"
                      aria-invalid={!!errors.billing_state}
                      aria-describedby={errors.billing_state ? 'billing-state-error' : undefined}
                    />
                    {errors.billing_state && (
                      <p
                        id="billing-state-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.billing_state}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="billing_postal_code">Billing Zip/Postal Code *</Label>
                    <Input
                      id="billing_postal_code"
                      value={formData.billing_postal_code}
                      onChange={(e) => handleChange('billing_postal_code', e.target.value)}
                      placeholder="Postal Code"
                      aria-required="true"
                      aria-invalid={!!errors.billing_postal_code}
                      aria-describedby={
                        errors.billing_postal_code ? 'billing-postal-error' : undefined
                      }
                    />
                    {errors.billing_postal_code && (
                      <p
                        id="billing-postal-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.billing_postal_code}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="billing_country">Billing Country *</Label>
                    <Input
                      id="billing_country"
                      value={formData.billing_country}
                      onChange={(e) => handleChange('billing_country', e.target.value)}
                      placeholder="Country"
                      aria-required="true"
                      aria-invalid={!!errors.billing_country}
                      aria-describedby={
                        errors.billing_country ? 'billing-country-error' : undefined
                      }
                    />
                    {errors.billing_country && (
                      <p
                        id="billing-country-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.billing_country}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Shipping Address</h4>

                <AddressAutocomplete
                  id="shipping_address"
                  label="Shipping Street"
                  value={formData.shipping_street || ''}
                  onAddressSelect={(address: AddressDetails) => {
                    setFormData((prev) => ({
                      ...prev,
                      shipping_street: address.street,
                      shipping_city: address.city,
                      shipping_state: address.state,
                      shipping_postal_code: address.postal_code,
                      shipping_country: address.country,
                    }))
                  }}
                  onStreetChange={(street) => handleChange('shipping_street', street)}
                  placeholder="Start typing address..."
                />

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="shipping_city">Shipping City</Label>
                    <Input
                      id="shipping_city"
                      value={formData.shipping_city}
                      onChange={(e) => handleChange('shipping_city', e.target.value)}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_state">Shipping State/Province</Label>
                    <Input
                      id="shipping_state"
                      value={formData.shipping_state}
                      onChange={(e) => handleChange('shipping_state', e.target.value)}
                      placeholder="State"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="shipping_postal_code">Shipping Zip/Postal Code</Label>
                    <Input
                      id="shipping_postal_code"
                      value={formData.shipping_postal_code}
                      onChange={(e) => handleChange('shipping_postal_code', e.target.value)}
                      placeholder="Postal Code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_country">Shipping Country</Label>
                    <Input
                      id="shipping_country"
                      value={formData.shipping_country}
                      onChange={(e) => handleChange('shipping_country', e.target.value)}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Additional Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Number of Locations */}
              <div>
                <Label htmlFor="number_of_locations">Number of Locations</Label>
                <Input
                  id="number_of_locations"
                  type="number"
                  min="0"
                  value={formData.number_of_locations__c ?? ''}
                  onChange={(e) =>
                    handleChange(
                      'number_of_locations__c',
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  placeholder="0"
                  aria-label="Number of locations"
                />
              </div>

              {/* Digital Policy */}
              <div>
                <Label htmlFor="digital_policy">Digital Policy</Label>
                <select
                  id="digital_policy"
                  value={formData.digital_policy__c}
                  onChange={(e) => handleChange('digital_policy__c', e.target.value)}
                  className={selectClassName}
                  aria-label="Digital policy"
                >
                  <option value="">Select...</option>
                  {digitalPolicies.map((policy) => (
                    <option key={policy} value={policy}>
                      {policy}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campaign */}
              <div>
                <Label htmlFor="campaign">Campaign</Label>
                <Input
                  id="campaign"
                  value={(formData as any).campaign__c || ''}
                  onChange={(e) => handleChange('campaign__c' as any, e.target.value)}
                  placeholder="Enter campaign"
                  aria-label="Campaign"
                />
              </div>

              {/* Type of Shop */}
              <div>
                <Label htmlFor="type_of_shop">Type of Shop</Label>
                <Input
                  id="type_of_shop"
                  value={(formData as any).type_of_shop__c || ''}
                  onChange={(e) => handleChange('type_of_shop__c' as any, e.target.value)}
                  placeholder="Enter type of shop"
                  aria-label="Type of shop"
                />
              </div>

              {/* Digital Accessibility Proficiency */}
              <div>
                <Label htmlFor="digital_proficiency">Digital Accessibility Proficiency</Label>
                <select
                  id="digital_proficiency"
                  value={formData.digital_accessibility_proficiency__c}
                  onChange={(e) =>
                    handleChange('digital_accessibility_proficiency__c', e.target.value)
                  }
                  className={selectClassName}
                  aria-label="Digital accessibility proficiency"
                >
                  <option value="">Select...</option>
                  {digitalAccessibilityProficiencies.map((prof) => (
                    <option key={prof} value={prof}>
                      {prof}
                    </option>
                  ))}
                </select>
              </div>

              {/* Current Digital Accessibility Vendor */}
              <div>
                <Label htmlFor="digital_vendor">Current Digital Accessibility Vendor</Label>
                <select
                  id="digital_vendor"
                  value={formData.current_digital_accessibility_vendor__c}
                  onChange={(e) =>
                    handleChange('current_digital_accessibility_vendor__c', e.target.value)
                  }
                  className={selectClassName}
                  aria-label="Current digital accessibility vendor"
                >
                  <option value="">Select...</option>
                  {digitalAccessibilityVendors.map((vendor) => (
                    <option key={vendor} value={vendor}>
                      {vendor}
                    </option>
                  ))}
                </select>
              </div>

              {/* Empty left column spacer */}
              <div />

              {/* House Account */}
              <div className="flex items-center space-x-2 pt-6">
                <input
                  id="house_account"
                  type="checkbox"
                  checked={formData.house_account__c === true}
                  onChange={(e) => handleChange('house_account__c', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  aria-label="House account"
                />
                <Label htmlFor="house_account" className="text-sm font-normal cursor-pointer">
                  House Account
                </Label>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 bg-muted px-3 py-1.5 rounded-sm">
              Description
            </h3>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Enter account description"
                rows={3}
              />
            </div>
          </div>

          {/* Duplicate Warning */}
          <DuplicateWarning entityType="Account" formData={formData as Record<string, unknown>} />

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
              aria-label="Cancel and close modal"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              aria-label={createMutation.isPending ? 'Creating account...' : 'Create account'}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
